import { Controller } from "@hotwired/stimulus";
import {
  GridApi,
  ColDef,
  IRowNode,
  ModuleRegistry,
  AllEnterpriseModule,
  LicenseManager,
  RowSelectedEvent,
} from "ag-grid-enterprise";
import type {
  SideBarDef,
  ToolPanelDef,
  MenuItemDef,
  DefaultMenuItem,
  GetMainMenuItemsParams,
} from "ag-grid-enterprise";
import { showConfirmModal } from "../ag_grid_shared/confirm_modal";

ModuleRegistry.registerModules([AllEnterpriseModule]);

export const MIN_GRID_HEIGHT = 400;
// Small breathing room at the viewport bottom — footer is pushed below the fold
export const GRID_BOTTOM_MARGIN = 24;

export class AgGridBaseController extends Controller {
  static targets = [
    "grid",
    "actionsMenu",
    "selectedCount",
    "viewToggleBasic",
    "viewToggleAdvanced",
    "viewToggleMobile",
    "columnsToggle",
    "filterToggle",
    "exportToggle",
    "exportDropdown",
  ];

  static values = {
    licenseKey:        String,
    columnDefs:        Array,
    gridOptions:       Object,
    gridStateUrl:      String,
    advancedColumnDefs: Array,
    selectionActions:  Array,
    followSelection:   Boolean,
  };

  declare readonly gridTarget: HTMLElement;
  declare readonly hasGridTarget: boolean;
  declare readonly actionsMenuTarget: HTMLElement;
  declare readonly hasActionsMenuTarget: boolean;
  declare readonly selectedCountTarget: HTMLElement;
  declare readonly hasSelectedCountTarget: boolean;
  declare readonly viewToggleBasicTarget: HTMLButtonElement;
  declare readonly viewToggleAdvancedTarget: HTMLButtonElement;
  declare readonly hasViewToggleBasicTarget: boolean;
  declare readonly hasViewToggleAdvancedTarget: boolean;
  declare readonly viewToggleMobileTarget: HTMLButtonElement;
  declare readonly hasViewToggleMobileTarget: boolean;
  declare readonly columnsToggleTarget: HTMLButtonElement;
  declare readonly hasColumnsToggleTarget: boolean;
  declare readonly filterToggleTarget: HTMLButtonElement;
  declare readonly hasFilterToggleTarget: boolean;
  declare readonly exportToggleTarget: HTMLButtonElement;
  declare readonly exportDropdownTarget: HTMLElement;
  declare readonly hasExportToggleTarget: boolean;
  declare readonly hasExportDropdownTarget: boolean;

  declare readonly licenseKeyValue: string;
  declare readonly columnDefsValue: ColDef[];
  declare readonly gridOptionsValue: Record<string, unknown>;
  declare readonly gridStateUrlValue: string;
  declare readonly advancedColumnDefsValue: ColDef[];
  declare readonly selectionActionsValue: Array<{
    label: string;
    url?: string;
    urlTemplate?: string;
    urlFromRowField?: string;
    icon?: string;
    method?: string;
    rel?: string;
    confirm?: string;
    turbo_frame?: string;
    target?: string;
    appendSelectedIds?: boolean;
    appendSelectedIdsParam?: string;
    minSelection?: number;
    maxSelection?: number;
    batchDelete?: boolean;
    requireAllRowsField?: string;
    refreshAfterDownload?: boolean;
  }>;
  declare readonly followSelectionValue: boolean;

  protected gridApi: GridApi | null = null;

  protected readonly rowSelectableCallback = (node: IRowNode): boolean =>
    (node.data as Record<string, unknown>)?.row_selectable !== false;
  protected isBasicMode = true;
  protected isRestoringState = false;
  protected pendingAutoSize = false;
  protected hasRowSelection = false;
  protected resizeHandler: (() => void) | null = null;
  protected outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  protected gridBlurHandler: ((e: MouseEvent) => void) | null = null;
  private actionAjaxSuccessHandler: ((e: Event) => void) | null = null;
  private modalRefreshObserver: MutationObserver | null = null;
  private lastClickX: number | null = null;
  // getSelectedNodes() is ordered by row index, not by selection chronology — checking
  // an earlier row after a later one otherwise anchors the menu to the wrong row.
  private followAnchorNodeId: string | null = null;
  private pendingFollowScrollFrame = false;
  private pendingAutoSizeNonFlexFrame = false;

  connect() {
    if (this.licenseKeyValue) {
      LicenseManager.setLicenseKey(this.licenseKeyValue);
    }
    this.outsideClickHandler = (e: MouseEvent) => {
      const exportOpen = this.hasExportDropdownTarget && !this.exportDropdownTarget.hidden;
      if (exportOpen) {
        if (
          !this.exportToggleTarget.contains(e.target as Node) &&
          !this.exportDropdownTarget.contains(e.target as Node)
        ) {
          this.closeExportDropdown();
        }
      }
    };
    document.addEventListener("click", this.outsideClickHandler);

    // Delegate from the persistent menu target so one listener survives every
    // renderActions() rebuild of the actions list underneath it.
    if (this.hasActionsMenuTarget) {
      this.actionAjaxSuccessHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.matches(".ag-grid-component-action-link[data-remote]")) return;
        if (target.matches('[rel~="modal:open"]')) return;
        this.refreshAfterRemoteAction();
      };
      this.actionsMenuTarget.addEventListener("ajax:success", this.actionAjaxSuccessHandler);
    }

    // jquery.modal mounts its content outside this grid's DOM, and its .js.erb response
    // detaches that content mid-dispatch — no listener outside it ever sees ajax:success.
    // Bind directly to data-ag-grid-refresh links as they're inserted instead.
    this.modalRefreshObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (!(mutation.target as HTMLElement).closest?.(".jquery-modal")) continue;
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          const targets = node.matches("[data-ag-grid-refresh]")
            ? [node]
            : Array.from(node.querySelectorAll("[data-ag-grid-refresh]"));
          targets.forEach((el) => {
            el.addEventListener("ajax:success", () => this.refreshAfterRemoteAction(), { once: true });
          });
        });
      }
    });
    this.modalRefreshObserver.observe(document.body, { childList: true, subtree: true });
  }

  disconnect() {
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.outsideClickHandler) {
      document.removeEventListener("click", this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    if (this.gridBlurHandler) {
      document.removeEventListener("mousedown", this.gridBlurHandler);
      this.gridBlurHandler = null;
    }
    if (this.actionAjaxSuccessHandler && this.hasActionsMenuTarget) {
      this.actionsMenuTarget.removeEventListener("ajax:success", this.actionAjaxSuccessHandler);
      this.actionAjaxSuccessHandler = null;
    }
    if (this.modalRefreshObserver) {
      this.modalRefreshObserver.disconnect();
      this.modalRefreshObserver = null;
    }
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  }

  // Called after a non-modal remote action (e.g. Unsubscribe) completes. No-op by
  // default — SSRM overrides this to refresh via refreshServerSide().
  protected refreshAfterRemoteAction(): void {
    // Intentionally empty in the base controller.
  }

  // Download Keys is a plain file-download GET, not an XHR — no ajax:success ever fires.
  // Fetch it manually so we get a real completion signal to refresh the grid on.
  private async downloadAndRefresh(url: string): Promise<void> {
    let response: Response;
    try {
      response = await fetch(url, { credentials: "same-origin" });
    } catch {
      alert("Download failed. Please try again.");
      return;
    }
    if (!response.ok) {
      alert("Download failed. Please try again.");
      return;
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match ? match[1] : "download";
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    this.refreshAfterRemoteAction();
  }

  // ── Toolbar actions ────────────────────────────────────────────────────────

  toggleColumnsPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "columns";
    if (isOpen) {
      this.gridApi.closeToolPanel();
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    } else {
      // openToolPanel() only shows the inner panel, not the side bar container itself.
      this.gridApi.setSideBarVisible(true);
      this.gridApi.openToolPanel("columns");
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleFilterPanel() {
    if (!this.gridApi) return;
    this.closeExportDropdown();
    const isOpen = this.gridApi.getOpenedToolPanel() === "filters-new";
    if (isOpen) {
      this.gridApi.closeToolPanel();
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    } else {
      // See toggleColumnsPanel() above.
      this.gridApi.setSideBarVisible(true);
      this.gridApi.openToolPanel("filters-new");
      if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "true");
      if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    }
  }

  toggleViewMode() {
    if (this.isBasicMode) {
      this.switchToAdvanced();
    } else {
      this.switchToBasic();
    }
  }

  toggleExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    const isOpen = !this.exportDropdownTarget.hidden;
    // Close tool panels and reset their buttons so they don't appear active
    // while the export dropdown is open.
    this.gridApi?.closeToolPanel();
    if (this.hasColumnsToggleTarget) this.columnsToggleTarget.setAttribute("aria-pressed", "false");
    if (this.hasFilterToggleTarget) this.filterToggleTarget.setAttribute("aria-pressed", "false");
    if (isOpen) {
      this.closeExportDropdown();
    } else {
      this.exportDropdownTarget.hidden = false;
      if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  // ── Abstract — concrete controllers must override ──────────────────────────

  switchToBasic(): void {
    throw new Error(`${this.constructor.name} must implement switchToBasic()`);
  }

  switchToAdvanced(): void {
    throw new Error(`${this.constructor.name} must implement switchToAdvanced()`);
  }

  // ── Protected helpers ──────────────────────────────────────────────────────

  protected setActiveToggle(mode: "basic" | "advanced") {
    if (!this.hasViewToggleBasicTarget || !this.hasViewToggleAdvancedTarget) return;
    const ACTIVE = "ag-grid-view-toggle__btn--active";
    if (mode === "basic") {
      this.viewToggleBasicTarget.classList.add(ACTIVE);
      this.viewToggleAdvancedTarget.classList.remove(ACTIVE);
    } else {
      this.viewToggleAdvancedTarget.classList.add(ACTIVE);
      this.viewToggleBasicTarget.classList.remove(ACTIVE);
    }
    if (this.hasViewToggleMobileTarget) {
      this.viewToggleMobileTarget.textContent = mode === "basic" ? "Basic" : "Advanced";
    }
  }

  protected closeExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    this.exportDropdownTarget.hidden = true;
    if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "false");
  }

  protected initDynamicHeight() {
    this.resizeHandler = () => this.updateGridHeight();
    window.addEventListener("resize", this.resizeHandler);
    // Iterate until height converges — necessary when the grid is inside a flex-centered
    // container (e.g. a modal) where the container's position depends on the grid's own
    // height (circular dependency). Page-mounted grids converge in 1-2 passes.
    let passes = 0;
    const measure = () => {
      const changed = this.updateGridHeight();
      if (changed && ++passes < 3) requestAnimationFrame(measure);
    };
    requestAnimationFrame(measure);
  }

  protected updateGridHeight(): boolean {
    if (!this.hasGridTarget) return false;
    const rect = this.gridTarget.getBoundingClientRect();
    const available = window.innerHeight - rect.top - GRID_BOTTOM_MARGIN;
    const newHeight = Math.max(available, MIN_GRID_HEIGHT);
    const prevHeight = parseFloat(this.gridTarget.style.height) || 0;
    if (Math.abs(newHeight - prevHeight) < 1) return false;
    this.gridTarget.style.height = `${newHeight}px`;
    return true;
  }

  protected syncPanelButtonStates() {
    if (!this.gridApi) return;
    const openPanel = this.gridApi.getOpenedToolPanel();
    if (this.hasColumnsToggleTarget) {
      this.columnsToggleTarget.setAttribute("aria-pressed", openPanel === "columns" ? "true" : "false");
    }
    if (this.hasFilterToggleTarget) {
      this.filterToggleTarget.setAttribute("aria-pressed", openPanel === "filters-new" ? "true" : "false");
    }
  }

  // Avoids AG-Grid's "count(Count)" aggFunc-prefixed header label on value columns.
  protected readonly suppressAggFuncInHeader = true;

  // Prevents a stuck wrap-text/auto-height header once Pivot Mode has been toggled at least
  // once. Spread into `defaultColDef` and `autoGroupColumnDef`.
  protected readonly pivotHeaderQuirkColDefOverrides = {
    wrapHeaderText: false,
    autoHeaderHeight: false,
  } as const;

  protected autoSizeNonFlexColumns() {
    if (!this.gridApi) return;
    // Called on every onCellValueChanged — a multi-cell paste fires that once per cell,
    // so without coalescing, an N-cell paste would trigger N full-grid autosize passes.
    if (this.pendingAutoSizeNonFlexFrame) return;
    this.pendingAutoSizeNonFlexFrame = true;
    requestAnimationFrame(() => {
      this.pendingAutoSizeNonFlexFrame = false;
      if (!this.gridApi) return;
      const visibleCols = this.gridApi.getAllDisplayedColumns() ?? [];
      if (visibleCols.length === 0) return;
      // Row Numbers is a fixed-width utility column, not a data column — autosizing it
      // to fit its own rendered content undoes its configured width (e.g. rowNumbers.width).
      const colIds = visibleCols
        .map((col) => col.getColId())
        .filter((colId) => colId !== "ag-Grid-RowNumbersColumn");
      this.gridApi.autoSizeColumns(colIds);

      // Autosize only measures the column's current (usually unsorted) header, with no
      // allowance for the sort icon that appears once a column is actively sorted -- pad
      // every column with a little slack up front so sorting doesn't wrap/overlap the label.
      const sortIconAllowance = 5;
      this.gridApi.applyColumnState({
        state: colIds.map((colId) => ({
          colId,
          width: (this.gridApi?.getColumn(colId)?.getActualWidth() ?? 0) + sortIconAllowance,
        })),
      });

      const containerWidth = this.gridTarget.clientWidth;
      const totalColWidth = visibleCols.reduce((sum, col) => sum + col.getActualWidth(), 0);
      if (totalColWidth < containerWidth) {
        const lastColId = colIds[colIds.length - 1];
        this.gridApi.applyColumnState({
          state: [{ colId: lastColId, flex: 1 }],
        });
      }
    });
  }

  protected renderActions(selectedRows: Array<Record<string, unknown>>): void {
    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (!actionsContainer) return;

    actionsContainer.innerHTML = "";

    type RowAction = Record<string, string> & {
      appendSelectedIds?: boolean;
      appendSelectedIdsParam?: string;
    };
    type StaticAction = typeof this.selectionActionsValue[number];

    const resolveSelectedIdsUrl = (action: RowAction | StaticAction, baseUrl: string): string => {
      if (!action.appendSelectedIds || selectedIds.length === 0) return baseUrl;
      const param = action.appendSelectedIdsParam ?? "ids";
      const existing = new Set(new URLSearchParams(baseUrl.split("?")[1] ?? "").getAll(`${param}[]`));
      const idsToAppend = selectedIds.filter((id) => !existing.has(String(id)));
      if (idsToAppend.length === 0) return baseUrl;
      const qs = idsToAppend.map((id) => `${param}[]=${id}`).join("&");
      return baseUrl + (baseUrl.includes("?") ? `&${qs}` : `?${qs}`);
    };

    const rowActions = selectedRows.map((row) =>
      Array.isArray(row.actions) ? (row.actions as RowAction[]) : []
    );

    const selectedIds = selectedRows
      .map((row) => row.id)
      .filter((id) => id !== undefined && id !== null) as (string | number)[];

    const count = selectedRows.length;

    const firstRowActions = rowActions[0] ?? [];
    const commonRowActions =
      rowActions.length === 1
        ? firstRowActions
        : firstRowActions.filter((action) =>
            Boolean(action.appendSelectedIds) &&
            rowActions.slice(1).every((otherActions) =>
              otherActions.some((a) => a.label === action.label)
            )
          );
    const filteredStaticActions = this.selectionActionsValue.filter((action) => {
      if (action.minSelection !== undefined && count < action.minSelection) return false;
      if (action.maxSelection !== undefined && count > action.maxSelection) return false;
      if (action.requireAllRowsField) {
        if (!selectedRows.every((row) => row[action.requireAllRowsField!])) return false;
      }
      if (action.urlFromRowField) {
        if (!selectedRows[0]?.[action.urlFromRowField]) return false;
      }
      return true;
    });

    // commonRowActions is always [] for grids with no per-row actions (org table) — the
    // `&&` only ever suppresses real per-row actions (Voter Status) past a single row.
    const showRowActions = count === 1 && commonRowActions.length > 0;

    const title = document.createElement("div");
    title.className = "ag-grid-component-actions-menu__title";

    if (!showRowActions && filteredStaticActions.length === 0) {
      title.textContent = "Actions unavailable for selection";
      actionsContainer.appendChild(title);
      return;
    }

    title.textContent = "Actions";
    actionsContainer.appendChild(title);

    const buildLink = (action: RowAction | StaticAction, url: string) => {
      const link = document.createElement("a");
      link.className = "ag-grid-component-action-link";
      link.href = url;

      if (action.method && action.method !== "get") {
        link.dataset.method = action.method;
        link.dataset.remote = "true";
      }
      if (action.rel) {
        link.rel = action.rel;
      }
      if (action.target) {
        link.target = action.target;
      }
      if (action.turbo_frame) {
        const frameId = action.turbo_frame;
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const frame = document.querySelector<HTMLElement>(`turbo-frame#${frameId}`);
          if (frame) frame.setAttribute("src", url);
        });
      } else if (action.confirm) {
        let bypass = false;
        link.addEventListener("click", async (e) => {
          if (bypass) return;
          e.preventDefault();
          e.stopPropagation();
          const ok = await showConfirmModal({ message: action.confirm });
          if (!ok) return;
          // Download Keys has no XHR to hook a refresh onto — fetch+blob it instead.
          if (action.refreshAfterDownload) {
            await this.downloadAndRefresh(url);
            return;
          }
          bypass = true;
          link.click();
          requestAnimationFrame(() => { bypass = false; });
        });
      }

      if (action.icon) {
        const icon = document.createElement("i");
        icon.className = `fa fa-${action.icon}`;
        icon.setAttribute("aria-hidden", "true");
        link.appendChild(icon);
      }

      link.appendChild(document.createTextNode(` ${action.label}`));
      return link;
    };

    if (showRowActions) {
      commonRowActions.forEach((action) => {
        actionsContainer.appendChild(buildLink(action, resolveSelectedIdsUrl(action, action.url || "#")));
      });
      return;
    }

    filteredStaticActions.forEach((action) => {
        if (action.batchDelete) {
          const btn = document.createElement("button");
          btn.className = "ag-grid-component-action-link";
          btn.type = "button";
          if (action.icon) {
            const icon = document.createElement("i");
            icon.className = `fa fa-${action.icon}`;
            icon.setAttribute("aria-hidden", "true");
            btn.appendChild(icon);
          }
          btn.appendChild(document.createTextNode(` ${action.label}`));
          btn.addEventListener("click", async () => {
            const msg = action.confirm ?? `Remove ${selectedIds.length} organization(s)? This cannot be undone.`;
            if (!window.confirm(msg)) return;
            const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
            for (const id of selectedIds) {
              await fetch(`/organizations/${id}`, {
                method: "DELETE",
                headers: { "X-CSRF-Token": token, "X-Requested-With": "XMLHttpRequest" },
              });
            }
            window.location.reload();
          });
          actionsContainer.appendChild(btn);
          return;
        }

        let url = action.url || "#";
        if (action.urlFromRowField) {
          url = String(selectedRows[0]?.[action.urlFromRowField] ?? "#");
        } else if (action.urlTemplate) {
          url = action.urlTemplate.replace("{id}", String(selectedIds[0] ?? ""));
        } else {
          url = resolveSelectedIdsUrl(action, url);
        }
        actionsContainer.appendChild(buildLink(action, url));
      });
  }

  protected clearActions(): void {
    const actionsContainer = this.actionsMenuTarget.querySelector(
      ".ag-grid-component-actions-menu__actions"
    );
    if (actionsContainer) {
      actionsContainer.innerHTML = "";
    }
  }

  protected buildSideBarConfig(hasAdvanced: boolean): false | SideBarDef {
    if (!hasAdvanced) return false;
    const toolPanels: ToolPanelDef[] = [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
        toolPanelParams: { suppressColumnSelectAll: false },
      },
      {
        id: "filters-new",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agNewFiltersToolPanel",
      },
    ];
    return { toolPanels, defaultToolPanel: "" };
  }

  // "Reset Columns" doesn't reset pivotMode by default (it's not part of column state).
  protected withPivotAwareResetColumns(
    params: GetMainMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    return (params.defaultItems ?? []).map((item) => {
      if (item !== "resetColumns") return item;
      return {
        // Matches AG-Grid's own default label (no locale API to look it up dynamically).
        name: "Reset Columns",
        action: () => {
          params.api.resetColumnState();
          if (params.api.isPivotMode()) {
            params.api.setGridOption("pivotMode", false);
          }
          // resetColumnState() reverts widths to their colDef-declared value (usually
          // none, so AG-Grid's generic default) -- re-fit to content, same as initial load.
          this.autoSizeNonFlexColumns();
        },
      } satisfies MenuItemDef;
    });
  }

  // Editable grids (voter-list edit, growable, provision) are too short/narrow for
  // AG-Grid's full column menu to lay out without clipping — keep only Reset Columns.
  protected resetColumnsOnlyMainMenuItems(
    params: GetMainMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] {
    return this.withPivotAwareResetColumns({ ...params, defaultItems: ["resetColumns"] });
  }

  protected basicColDefs(columnDefs: ColDef[]): ColDef[] {
    // enableRowGroup/enablePivot keep a header draggable (ghost + all) even with
    // suppressMovable set — AG-Grid's workOutDraggable() ORs them in regardless.
    return columnDefs.map((c) => ({
      ...c,
      suppressMovable: true,
      enableRowGroup: false,
      enablePivot: false,
    }));
  }

  // Give action cell renderers text to avoid warning (only action buttons, no need for text)
  protected withActionsValueFormatter(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((c) =>
      c.cellRenderer === "actionsCellRenderer" && c.valueFormatter === undefined
        ? { ...c, valueFormatter: () => "" }
        : c
    );
  }

  // Columns flagged with context.rankField (e.g. role hierarchy) sort by that numeric
  // row field instead of the displayed string, so ascending/descending follows the
  // domain hierarchy rather than alphabetical order.
  protected withRankComparator(columnDefs: ColDef[]): ColDef[] {
    return columnDefs.map((c) => {
      const context = c.context as Record<string, unknown> | undefined;
      const rankField = context?.rankField;
      if (typeof rankField !== "string") return c;

      const valueOrder = Array.isArray(context?.valueOrder) ? (context.valueOrder as string[]) : null;

      return {
        ...c,
        // agSetColumnFilter reuses this comparator to sort its value list, calling it
        // with only (valueA, valueB) — no row nodes — so rank by context.valueOrder then.
        comparator: (valueA: unknown, valueB: unknown, nodeA?: IRowNode, nodeB?: IRowNode) => {
          const rankA = nodeA?.data?.[rankField];
          const rankB = nodeB?.data?.[rankField];
          if (rankA !== undefined && rankB !== undefined) {
            return Number(rankA) - Number(rankB);
          }
          if (valueOrder) {
            return valueOrder.indexOf(String(valueA)) - valueOrder.indexOf(String(valueB));
          }
          return String(valueA ?? "").localeCompare(String(valueB ?? ""));
        },
      };
    });
  }

  // Opt-in: moves the actions menu next to the last-selected row instead of its pinned corner.
  protected setupFollowSelection(): void {
    if (!this.followSelectionValue) return;
    if (this.hasActionsMenuTarget) {
      this.actionsMenuTarget.classList.add("ag-grid-component-actions-menu--follow");
    }
    if (!this.hasGridTarget) return;
    this.gridTarget.addEventListener("mousedown", (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(".ag-selection-checkbox")) return;
      this.lastClickX = e.clientX;
    });
    // Grid's internal viewport scrolls independently of the page — track it so the
    // menu can hide/reposition relative to its anchor row.
    this.gridApi?.addEventListener("bodyScroll", () => this.scheduleFollowScrollUpdate());
    this.gridApi?.addEventListener("rowSelected", (e: RowSelectedEvent) => this.handleFollowRowSelected(e));
  }

  private getLoadedSelectedNodes(): IRowNode[] {
    if (!this.gridApi) return [];
    const nodes: IRowNode[] = [];
    const displayedCount = this.gridApi.getDisplayedRowCount();
    for (let i = 0; i < displayedCount; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (node && !node.group && node.isSelected()) nodes.push(node);
    }
    return nodes;
  }

  // Tracks whichever row was most recently checked, independent of getSelectedNodes()'s
  // row-index ordering; unchecking it falls back to whatever else is still selected.
  private handleFollowRowSelected(event: RowSelectedEvent): void {
    if (!this.followSelectionValue) return;
    const node = event.node;
    if (node.isSelected()) {
      this.followAnchorNodeId = node.id ?? null;
    } else if (node.id === this.followAnchorNodeId) {
      const remaining = this.getLoadedSelectedNodes();
      this.followAnchorNodeId = remaining.length > 0 ? remaining[remaining.length - 1].id ?? null : null;
    }
  }

  private getFollowAnchorNode(): IRowNode | null {
    if (!this.gridApi) return null;
    if (this.followAnchorNodeId) {
      const node = this.gridApi.getRowNode(this.followAnchorNodeId);
      if (node?.isSelected()) return node;
    }
    const selectedNodes = this.getLoadedSelectedNodes();
    return selectedNodes.length > 0 ? selectedNodes[selectedNodes.length - 1] : null;
  }

  private scheduleFollowScrollUpdate(): void {
    if (this.pendingFollowScrollFrame) return;
    this.pendingFollowScrollFrame = true;
    requestAnimationFrame(() => {
      this.pendingFollowScrollFrame = false;
      this.updateFollowScrollVisibility();
    });
  }

  protected updateFollowScrollVisibility(): void {
    if (!this.followSelectionValue || !this.gridApi || !this.hasActionsMenuTarget) return;

    // getFollowAnchorNode() trips warning #199 during selectAll — use the SSRM-provided
    // anchor instead so the menu still follows scroll.
    const anchorNode = this.isSelectAllActive() ? this.getSelectAllAnchorNode() : this.getFollowAnchorNode();
    if (!anchorNode) return; // handleSelectionChanged already hides the menu when nothing's selected

    const rowEl = this.gridTarget.querySelector(
      `[row-id="${CSS.escape(String(anchorNode.id))}"]`
    ) as HTMLElement | null;
    // Row-index range APIs include AG-Grid's offscreen row buffer, so compare actual
    // geometry against the scrollable viewport instead.
    const viewportEl = this.gridTarget.querySelector(".ag-body-viewport") as HTMLElement | null;

    if (!rowEl || !viewportEl) {
      this.actionsMenuTarget.style.display = "none";
      return;
    }

    const rowRect = rowEl.getBoundingClientRect();
    const viewportRect = viewportEl.getBoundingClientRect();
    const isVisible = rowRect.bottom > viewportRect.top && rowRect.top < viewportRect.bottom;

    this.actionsMenuTarget.style.display = isVisible ? "" : "none";
    // Anchor to the row's own position, not the last mousedown — the two can drift
    // apart during fast scrolling.
    if (isVisible) this.positionMenuNearClick(rowRect.top + rowRect.height / 2, this.lastClickX ?? 0);
  }

  // Overridden by SSRM (getServerSideSelectionState().selectAll can be true there, with no
  // single row to track); always false for CSRM, which has no such concept.
  protected isSelectAllActive(): boolean {
    return false;
  }

  // Overridden by SSRM to anchor on the last loaded row during a true select-all. Base default
  // is unreachable in practice since isSelectAllActive() is always false here.
  protected getSelectAllAnchorNode(): IRowNode | null {
    return null;
  }

  protected positionMenuNearClick(clientY: number, clientX: number): void {
    const menu = this.actionsMenuTarget;
    const wrapper = menu.parentElement;
    if (!wrapper) return;

    const menuWidth = menu.offsetWidth || 275;
    const menuHeight = menu.offsetHeight || 220;

    const wrapperRect = wrapper.getBoundingClientRect();
    const selectionColWidth = this.gridApi?.getAllDisplayedColumns()[0]?.getActualWidth() ?? 48;

    // Clamp against both viewport bottom and the grid's own bottom edge, so the menu
    // can't overflow past where the grid ends even if the viewport has room below it.
    const viewportMaxTop = window.innerHeight - menuHeight - 16;
    const gridRect = this.hasGridTarget ? this.gridTarget.getBoundingClientRect() : null;
    const gridMaxTop = gridRect ? gridRect.bottom - menuHeight - 8 : viewportMaxTop;
    const maxTop = Math.min(viewportMaxTop, gridMaxTop);
    const top = Math.min(Math.max(clientY + 15, 0), Math.max(maxTop, 0));

    const minLeft = wrapperRect.left + selectionColWidth + 8;
    const maxLeft = window.innerWidth - menuWidth - 8;
    const left = Math.min(Math.max(clientX + 8, minLeft), Math.max(maxLeft, minLeft));

    // Absolute (not fixed) positioning is relative to the wrapper's box, so the menu
    // scrolls away with the page instead of staying glued to the screen.
    menu.style.top = `${top - wrapperRect.top}px`;
    menu.style.left = `${left - wrapperRect.left}px`;
    menu.style.right = "auto";
    menu.style.bottom = "auto";
  }

  // Clears inline positioning, restoring the CSS-driven default (pinned corner).
  protected resetMenuPosition(): void {
    if (!this.hasActionsMenuTarget) return;
    const menu = this.actionsMenuTarget;
    menu.style.top = "";
    menu.style.left = "";
    menu.style.right = "";
    menu.style.bottom = "";
  }
}
